import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Handshake,
  TrendingUp,
  DollarSign,
  HeadphonesIcon,
  Cpu,
  Globe,
  Building2,
  Users,
  Code,
  CheckCircle,
  ArrowRight,
  Send,
  Briefcase,
  MapPin,
  Mail,
  Phone,
  Link as LinkIcon,
  Calendar,
  Target,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import SEO from './SEO';
import PageLayout, { PageHero } from './layout/PageLayout';

const PartnerProgram = ({ onBack, onLogin, onRegister, navigateTo }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    applicant_name: '',
    email: '',
    phone: '',
    country: '',
    website: '',
    years_in_business: '',
    industry_focus: '',
    estimated_clients_per_year: '',
    client_base_description: '',
    why_partner: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/partners/apply`, formData);
      setSubmitted(true);
    } catch (err) {
      // Handle Pydantic validation errors (array of objects) or string errors
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Extract error messages from validation error objects
        const messages = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        setError(messages);
      } else if (typeof detail === 'object' && detail !== null) {
        setError(detail.msg || detail.message || 'Validation error occurred.');
      } else {
        setError(detail || 'Failed to submit application. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Building2,
      title: 'Test Automation Platform',
      description: 'Explore API testing, browser flows, and test management in a shared platform.'
    },
    {
      icon: TrendingUp,
      title: 'Recurring Revenue',
      description: 'Build sustainable income streams with recurring commissions on active client contracts.'
    },
    {
      icon: DollarSign,
      title: 'High-Performance Commission',
      description: 'Competitive commission structure rewarding top performers with premium rates.'
    },
    {
      icon: HeadphonesIcon,
      title: 'Dedicated Support',
      description: 'Access to dedicated technical and sales support to help close enterprise deals.'
    },
    {
      icon: Cpu,
      title: 'AI-Powered Innovation',
      description: 'Leverage cutting-edge AI technology that converts natural language into automated tests.'
    },
    {
      icon: Globe,
      title: 'Global Expansion',
      description: 'Discuss opportunities to introduce Lexa to teams in your market.'
    }
  ];

  const partnerTypes = [
    {
      icon: Users,
      title: 'Sales Partner',
      description: 'Introduce enterprise clients and earn commission on active contracts. Ideal for consultants, sales professionals, and business development experts.',
      features: ['Lead referral commission', 'Deal registration protection', 'Sales enablement materials']
    },
    {
      icon: MapPin,
      title: 'Regional Partner',
      description: 'Represent Lexa in a specific country or region. Manage local sales, relationships, and market development.',
      features: ['Exclusive territory rights', 'Local marketing support', 'Priority lead routing']
    },
    {
      icon: Code,
      title: 'Technology Partner',
      description: 'Integrate, deploy, or collaborate on infrastructure and technical services. Perfect for system integrators and tech consultancies.',
      features: ['API access & documentation', 'Technical certification', 'Joint solution development']
    }
  ];

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Spain', 'Italy',
    'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway', 'Denmark',
    'Finland', 'Ireland', 'Portugal', 'Poland', 'Australia', 'New Zealand', 'Japan',
    'South Korea', 'Singapore', 'India', 'Brazil', 'Mexico', 'Argentina', 'Chile',
    'Colombia', 'South Africa', 'Israel', 'United Arab Emirates', 'Saudi Arabia',
    'Turkey', 'Greece', 'Other'
  ];

  return (
    <PageLayout onBack={onBack} onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Lexa Partner Program | Enterprise AI Test Automation Partnerships"
        description="Explore partnership opportunities with Lexa and tell us how your business would like to collaborate."
        keywords="partner program, test automation partnership, enterprise software partner, AI testing partner, SaaS partnership"
      />

      <PageHero
        badge={<><Handshake className="w-4 h-4" /> Partner Program</>}
        title="Build together."
        titleHighlight="Become a Lexa partner."
        subtitle="Explore a partnership around test automation. Tell us about your business and how you would like to work with Lexa."
      >
        <Button
          onClick={() => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' })}
          size="lg"
          className="mt-8 rounded-full bg-violet-700 px-8 py-6 text-white hover:bg-violet-800"
        >
          Apply to Become a Partner
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </PageHero>

      {/* Why Partner Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Partner with Lexa
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join a partnership that delivers value, innovation, and sustainable growth opportunities.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="p-6 bg-white border-gray-200 hover:border-purple-300 transition-all duration-300 hover:shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Types Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Partner Types
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose the partnership model that best fits your business and expertise.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {partnerTypes.map((type, index) => (
              <Card key={index} className="p-8 bg-white border-gray-200 hover:border-purple-300 transition-all duration-300 group hover:shadow-lg">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <type.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{type.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{type.description}</p>
                <ul className="space-y-3">
                  {type.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Structure Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <DollarSign className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Commission Structure
          </h2>
          <p className="text-xl text-gray-700 mb-6">
            Competitive commission structure based on performance and contract value.
          </p>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Our partner program offers attractive recurring revenue opportunities for active client relationships. 
            As your referred clients continue to use Lexa, you continue to earn. Top-performing partners enjoy 
            enhanced commission rates, priority support, and exclusive access to enterprise deals.
          </p>
        </div>
      </section>

      {/* Application Form Section */}
      <section id="apply-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Apply to Become a Partner
            </h2>
            <p className="text-gray-600">
              Complete the form below and our partnership team will review your application.
            </p>
          </div>

          {submitted ? (
            <Card className="p-12 bg-white border-gray-200 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Application Submitted!</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Thank you for your interest in partnering with Lexa. We've received your application and 
                will review it shortly. You'll receive a confirmation email with next steps.
              </p>
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                Status: Pending Review
              </Badge>
            </Card>
          ) : (
            <Card className="p-8 bg-white border-gray-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Company Name */}
                  <div>
                    <Label className="text-gray-700 mb-2 block">
                      <Building2 className="h-4 w-4 inline mr-2" />
                      Company Name *
                    </Label>
                    <Input
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                      placeholder="Your company name"
                    />
                  </div>

                  {/* Applicant Name */}
                  <div>
                    <Label className="text-gray-700 mb-2 block">
                      <Users className="h-4 w-4 inline mr-2" />
                      Applicant Full Name *
                    </Label>
                    <Input
                      name="applicant_name"
                      value={formData.applicant_name}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                      placeholder="Your full name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <Label className="text-gray-700 mb-2 block">
                      <Mail className="h-4 w-4 inline mr-2" />
                      Email *
                    </Label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                      placeholder="business@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <Label className="text-gray-700 mb-2 block">
                      <Phone className="h-4 w-4 inline mr-2" />
                      Phone Number *
                    </Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <Label className="text-gray-700 mb-2 block">
                      <Globe className="h-4 w-4 inline mr-2" />
                      Country *
                    </Label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 px-3 bg-white border border-gray-300 text-gray-900 rounded-md focus:border-purple-500 focus:outline-none"
                    >
                      <option value="">Select a country</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>

                  {/* Website */}
                  <div>
                    <Label className="text-gray-700 mb-2 block">
                      <LinkIcon className="h-4 w-4 inline mr-2" />
                      Company Website
                    </Label>
                    <Input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                      placeholder="https://yourcompany.com"
                    />
                  </div>

                  {/* Years in Business */}
                  <div>
                    <Label className="text-gray-700 mb-2 block">
                      <Calendar className="h-4 w-4 inline mr-2" />
                      Years in Business *
                    </Label>
                    <select
                      name="years_in_business"
                      value={formData.years_in_business}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 px-3 bg-white border border-gray-300 text-gray-900 rounded-md focus:border-purple-500 focus:outline-none"
                    >
                      <option value="">Select years</option>
                      <option value="0-1">Less than 1 year</option>
                      <option value="1-3">1-3 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="5-10">5-10 years</option>
                      <option value="10+">10+ years</option>
                    </select>
                  </div>

                  {/* Industry Focus */}
                  <div>
                    <Label className="text-gray-700 mb-2 block">
                      <Briefcase className="h-4 w-4 inline mr-2" />
                      Industry Focus *
                    </Label>
                    <Input
                      name="industry_focus"
                      value={formData.industry_focus}
                      onChange={handleInputChange}
                      required
                      className="bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                      placeholder="e.g., Financial Services, Healthcare, E-commerce"
                    />
                  </div>

                  {/* Estimated Clients */}
                  <div className="md:col-span-2">
                    <Label className="text-gray-700 mb-2 block">
                      <Target className="h-4 w-4 inline mr-2" />
                      Estimated Potential Clients Per Year *
                    </Label>
                    <select
                      name="estimated_clients_per_year"
                      value={formData.estimated_clients_per_year}
                      onChange={handleInputChange}
                      required
                      className="w-full h-10 px-3 bg-white border border-gray-300 text-gray-900 rounded-md focus:border-purple-500 focus:outline-none"
                    >
                      <option value="">Select estimate</option>
                      <option value="1-5">1-5 clients</option>
                      <option value="5-10">5-10 clients</option>
                      <option value="10-25">10-25 clients</option>
                      <option value="25-50">25-50 clients</option>
                      <option value="50+">50+ clients</option>
                    </select>
                  </div>
                </div>

                {/* Client Base Description */}
                <div>
                  <Label className="text-gray-700 mb-2 block">
                    Describe Your Client Base *
                  </Label>
                  <Textarea
                    name="client_base_description"
                    value={formData.client_base_description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                    placeholder="Tell us about your typical clients, their size, industries, and testing needs..."
                  />
                </div>

                {/* Why Partner */}
                <div>
                  <Label className="text-gray-700 mb-2 block">
                    Why Do You Want to Partner with Lexa? *
                  </Label>
                  <Textarea
                    name="why_partner"
                    value={formData.why_partner}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="bg-white border-gray-300 text-gray-900 focus:border-purple-500"
                    placeholder="Share your motivation, experience with test automation, and how you envision the partnership..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-6 text-lg rounded-xl"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Submit Partner Application
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting this form, you agree to our Terms of Service and Privacy Policy.
                </p>
              </form>
            </Card>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

export default PartnerProgram;
